<template>
  <v-container fluid>
    <div class="d-flex align-center justify-space-between mb-3 page-header">
      <v-card-title class="text-h5 font-weight-bold mb-0">
        <v-icon left color="#4a3aff">mdi-account-group-outline</v-icon>
        {{ $t('workers.title') }}
      </v-card-title>
    </div>

    <v-alert v-if="message" :type="messageType" dense border="left" class="mb-4">
      {{ message }}
    </v-alert>

    <v-card class="rounded-lg workers-panel">
      <v-tabs v-model="tab" grow slider-color="#4a3aff" class="workers-tabs" data-tour="workers-tabs">
        <v-tab>{{ $t('workers.pending') }}</v-tab>
        <v-tab>{{ $t('workers.accepted') }}</v-tab>
        <v-tab>{{ $t('workers.rejected') }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="tab">

        <!-- 🔹 PENDIENTES -->
        <v-tab-item>
          <div class="worker-search-wrap">
            <v-text-field
              v-model="searchQuery"
              :placeholder="$t('workers.searchPlaceholder')"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              class="worker-search"
            />
          </div>

          <v-row class="workers-grid">
            <v-col
              v-for="req in filteredPending"
              :key="req.id"
              cols="12" md="6" lg="4"
            >
              <v-card class="pa-4 rounded-lg worker-card">
                <v-card-title class="worker-card-title">
                  <v-avatar color="#eae7f8" class="mr-3">
                    <v-icon color="#4a3aff">mdi-account</v-icon>
                  </v-avatar>
                  <div class="worker-identity">
                    <strong class="worker-name">{{ req.nombre }} {{ req.apellidos }}</strong>
                    <div class="grey--text text--darken-1 text-body-2 worker-email">
                      {{ req.email }}
                    </div>
                  </div>
                </v-card-title>

                <v-card-actions class="justify-end worker-card-actions">
                  <v-btn icon color="green" class="worker-action-btn" @click="acceptRequest(req)">
                    <v-icon>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon color="red" class="worker-action-btn" @click="rejectRequest(req)">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>

          <div v-if="!filteredPending.length" class="text-center py-6 grey--text">
            <v-icon size="48" color="#c5cae9">mdi-email-off-outline</v-icon>
            <div class="mt-2">{{ pending.length && isSearchActive ? $t('workers.emptySearch') : $t('workers.emptyPending') }}</div>
          </div>
        </v-tab-item>

        <!-- 🔹 ACEPTADOS -->
        <v-tab-item>
          <div class="worker-search-wrap">
            <v-text-field
              v-model="searchQuery"
              :placeholder="$t('workers.searchPlaceholder')"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              class="worker-search"
            />
          </div>

          <v-list dense>
            <v-list-item
              v-for="worker in filteredAccepted"
              :key="worker.id"
              class="rounded-lg my-1 worker-accepted"
            >
              <v-icon class="worker-list-icon" color="green">mdi-check-circle</v-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ worker.nombre }} {{ worker.apellidos }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ worker.email }}</v-list-item-subtitle>
              </v-list-item-content>

              <!-- ❌ Revocar acceso -->
              <v-btn icon color="red" class="worker-list-action" @click="revokeWorker(worker)">
                <v-icon>mdi-close-circle</v-icon>
              </v-btn>
            </v-list-item>

            <div v-if="!filteredAccepted.length" class="text-center py-6 grey--text">
              <v-icon size="40">mdi-account-question</v-icon>
              <div>{{ accepted.length && isSearchActive ? $t('workers.emptySearch') : $t('workers.emptyAccepted') }}</div>
            </div>
          </v-list>
        </v-tab-item>

        <!-- 🔹 RECHAZADOS -->
        <v-tab-item>
          <div class="worker-search-wrap">
            <v-text-field
              v-model="searchQuery"
              :placeholder="$t('workers.searchPlaceholder')"
              prepend-inner-icon="mdi-magnify"
              clearable
              dense
              outlined
              hide-details
              class="worker-search"
            />
          </div>

          <v-list dense>
            <v-list-item
              v-for="req in filteredRejected"
              :key="req.id"
              class="rounded-lg my-1 worker-rejected"
            >
              <v-icon class="worker-list-icon" color="red">mdi-close-circle</v-icon>
              <v-list-item-content>
                <v-list-item-title>
                  {{ req.nombre }} {{ req.apellidos }}
                </v-list-item-title>
                <v-list-item-subtitle>{{ req.email }}</v-list-item-subtitle>
              </v-list-item-content>

              <!-- ♻ Recuperar -->
              <v-btn icon color="green" class="worker-list-action" @click="restoreRequest(req)">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-list-item>

            <div v-if="!filteredRejected.length" class="text-center py-6 grey--text">
              <v-icon size="40">mdi-cancel</v-icon>
              <div>{{ rejected.length && isSearchActive ? $t('workers.emptySearch') : $t('workers.emptyRejected') }}</div>
            </div>
          </v-list>
        </v-tab-item>

      </v-tabs-items>
    </v-card>
  </v-container>
</template>

<script>
import { db } from "@/firebase";
import {
  collection,
  deleteDoc,
  updateDoc,
  doc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  setDoc,
  writeBatch
} from "firebase/firestore";
import { sendWorkerPushNotification } from "@/services/pushNotifications";
import { getUserErrorMessage } from "@/utils/errors";

export default {
  data() {
    return {
      tab: 0,
      searchQuery: "",
      message: "",
      messageType: "success",
      pending: [],
      rejected: [],
      accepted: [],
      unsubscribeRequests: null,
      unsubscribeWorkers: null,
      unsubscribeUserRequests: [],
      requestDocs: [],
      userRequestDocs: new Map(),
      userRequestDocsByQuery: [],
    };
  },

  computed: {
    companyId() {
      return this.$store.getters["auth/companyId"];
    },
    companyName() {
      return this.$store.getters["auth/companyName"];
    },
    normalizedSearchQuery() {
      return this.normalizeSearchText(this.searchQuery);
    },
    isSearchActive() {
      return this.normalizedSearchQuery.length >= 3;
    },
    filteredPending() {
      return this.filterWorkers(this.pending);
    },
    filteredAccepted() {
      return this.filterWorkers(this.accepted);
    },
    filteredRejected() {
      return this.filterWorkers(this.rejected);
    }
  },

  created() {
    this.subscribeAll();
  },

  beforeDestroy() {
    this.unsubscribeAll();
  },

  watch: {
    companyId() {
      this.subscribeAll();
    },
  },

  methods: {
    /* ===========================
     * LOADERS
     * =========================== */
    normalizeSearchText(value) {
      return String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
    },

    filterWorkers(workers) {
      if (!this.isSearchActive) return workers;

      return workers.filter((worker) => {
        const searchableText = this.normalizeSearchText([
          worker.nombre,
          worker.apellidos,
          worker.email
        ].join(" "));

        return searchableText.includes(this.normalizedSearchQuery);
      });
    },
    subscribeAll() {
      this.unsubscribeAll();
      this.pending = [];
      this.rejected = [];
      this.accepted = [];
      this.requestDocs = [];
      this.userRequestDocs = new Map();
      this.userRequestDocsByQuery = [];

      if (!this.companyId) return;

      this.subscribeRequests();
      this.subscribeWorkers();
    },

    unsubscribeAll() {
      if (this.unsubscribeRequests) this.unsubscribeRequests();
      if (this.unsubscribeWorkers) this.unsubscribeWorkers();
      this.unsubscribeUserRequests.forEach((unsubscribe) => unsubscribe());

      this.unsubscribeRequests = null;
      this.unsubscribeWorkers = null;
      this.unsubscribeUserRequests = [];
    },

    subscribeRequests() {
      if (!this.companyId) return;

      const requestsQuery = query(
        collection(db, "companyJoinRequests"),
        where("companyId", "==", this.companyId)
      );

      this.unsubscribeRequests = onSnapshot(
        requestsQuery,
        (snapshot) => {
          this.requestDocs = snapshot.docs.map(d => ({
            id: d.id,
            requestDocExists: true,
            ...d.data()
          }));
          this.recomputeRequests();
        },
        (error) => {
          this.showMessage(getUserErrorMessage(error, "workers.loadError", this.$i18n), "error");
        }
      );

      const userRequestQueries = ["pending", "rejected"].flatMap(status => ([
        query(
          collection(db, "users"),
          where("companyId", "==", this.companyId),
          where("companyStatus", "==", status)
        ),
        query(
          collection(db, "users"),
          where("companyRequest.companyId", "==", this.companyId),
          where("companyStatus", "==", status)
        )
      ]));

      this.userRequestDocsByQuery = userRequestQueries.map(() => new Map());

      this.unsubscribeUserRequests = userRequestQueries.map((userRequestQuery, index) => (
        onSnapshot(
          userRequestQuery,
          (snapshot) => {
            this.userRequestDocsByQuery[index] = new Map(
              snapshot.docs.map(userDoc => [userDoc.id, userDoc])
            );
            this.userRequestDocs = new Map(
              this.userRequestDocsByQuery.flatMap(userDocs => Array.from(userDocs.entries()))
            );
            this.recomputeRequests();
          },
          (error) => {
            this.showMessage(getUserErrorMessage(error, "workers.loadError", this.$i18n), "error");
          }
        )
      ));
    },

    recomputeRequests() {
      const existingByUserAndCompany = new Set(
        this.requestDocs.map(r => `${r.uid}_${r.companyId}`)
      );

      const userRequests = Array.from(this.userRequestDocs.values())
        .map(d => this.buildRequestFromUser(d))
        .filter(Boolean)
        .filter(r => !existingByUserAndCompany.has(`${r.uid}_${r.companyId}`));

      const all = [...this.requestDocs, ...userRequests];

      this.pending = all.filter(r => r.status === "pending");
      this.rejected = all.filter(r => r.status === "rejected");
    },

    buildRequestFromUser(userDoc) {
      const user = { uid: userDoc.id, ...userDoc.data() };
      const requestData = typeof user.companyRequest === "object" && user.companyRequest
        ? user.companyRequest
        : {};
      const requestCompanyId = requestData.companyId || user.companyId;

      if (requestCompanyId !== this.companyId) return null;

      return {
        id: typeof user.companyRequest === "string"
          ? user.companyRequest
          : `${user.uid}_${requestCompanyId}`,
        requestDocExists: false,
        uid: user.uid,
        companyId: requestCompanyId,
        companyName: requestData.companyName || user.companyName || this.companyName || "",
        nombre: user.nombre || "",
        apellidos: user.apellidos || "",
        email: user.email || "",
        role: user.role || "user",
        status: requestData.status || user.companyStatus,
        createdAt: requestData.createdAt || user.createdAt || null,
      };
    },

    subscribeWorkers() {
      if (!this.companyId) return;

      this.unsubscribeWorkers = onSnapshot(
        collection(db, "companies", this.companyId, "workers")
        ,
        (snapshot) => {
          this.accepted = snapshot.docs.map(d => ({
            id: d.id,
            ...d.data()
          }));
        },
        (error) => {
          this.showMessage(getUserErrorMessage(error, "workers.loadError", this.$i18n), "error");
        }
      );
    },

    /* ===========================
     * ACTIONS
     * =========================== */

    // ✅ Aceptar solicitud
    async acceptRequest(req) {
      try {
        await setDoc(doc(db, "companies", this.companyId, "workers", req.uid), {
          uid: req.uid,
          nombre: req.nombre,
          apellidos: req.apellidos,
          email: req.email,
          role: "worker",
          status: "accepted",
          acceptedAt: serverTimestamp(),
        });

        await updateDoc(doc(db, "users", req.uid), {
          companyId: this.companyId,
          companyName: req.companyName || this.companyName || "",
          companyStatus: "accepted",
          companyRequest: null,
        });

        if (req.requestDocExists) {
          await deleteDoc(doc(db, "companyJoinRequests", req.id));
        }

        sendWorkerPushNotification(this.companyId, req.uid, {
          notificationType: "company-accepted",
          title: this.$t("workers.acceptedNotificationTitle"),
          body: this.$t("workers.acceptedNotificationBody", {
            company: req.companyName || this.companyName || this.$t("workers.yourCompany"),
          }),
          data: {
            status: "accepted",
            companyId: this.companyId,
          },
        }).catch((error) => {
          console.warn(this.$t("workers.acceptedNotificationError"), error);
        });

      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "workers.acceptError", this.$i18n), "error");
      }
    },

    // ❌ Rechazar solicitud pendiente
    async rejectRequest(req) {
      try {
        if (req.requestDocExists) {
          await updateDoc(
            doc(db, "companyJoinRequests", req.id),
            { status: "rejected" }
          );
        } else {
          await setDoc(doc(db, "companyJoinRequests", req.id), {
            uid: req.uid,
            companyId: this.companyId,
            companyName: req.companyName || this.companyName || "",
            nombre: req.nombre,
            apellidos: req.apellidos,
            email: req.email,
            status: "rejected",
            createdAt: req.createdAt || serverTimestamp(),
          });
        }

        await updateDoc(doc(db, "users", req.uid), {
          companyId: this.companyId,
          companyName: req.companyName || this.companyName || "",
          companyStatus: "rejected",
          companyRequest: req.id,
        });

        sendWorkerPushNotification(this.companyId, req.uid, {
          notificationType: "company-rejected",
          title: this.$t("workers.rejectedNotificationTitle"),
          body: this.$t("workers.rejectedNotificationBody", {
            company: req.companyName || this.companyName || this.$t("workers.theCompany"),
          }),
          data: {
            status: "rejected",
            companyId: this.companyId,
          },
        }).catch((error) => {
          console.warn(this.$t("workers.rejectedNotificationError"), error);
        });

      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "workers.rejectError", this.$i18n), "error");
      }
    },

    // ❌ Revocar trabajador aceptado
    async revokeWorker(worker) {
      try {
        const requestId = `${worker.uid}_${this.companyId}`;
        const batch = writeBatch(db);

        batch.delete(doc(db, "companies", this.companyId, "workers", worker.id));

        batch.set(doc(db, "companyJoinRequests", requestId), {
          uid: worker.uid,
          companyId: this.companyId,
          companyName: this.companyName || "",
          nombre: worker.nombre,
          apellidos: worker.apellidos,
          email: worker.email,
          role: "worker",
          status: "rejected",
          createdAt: serverTimestamp(),
        });

        batch.update(doc(db, "users", worker.uid), {
          companyId: this.companyId,
          companyName: this.companyName || "",
          companyStatus: "rejected",
          companyRequest: requestId,
        });

        await batch.commit();
      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "workers.revokeError", this.$i18n), "error");
      }
    },

    // ♻ Recuperar solicitud rechazada
    async restoreRequest(req) {
      try {
        await updateDoc(
          doc(db, "companyJoinRequests", req.id),
          { status: "pending" }
        );

        await updateDoc(doc(db, "users", req.uid), {
          companyId: this.companyId,
          companyName: req.companyName || this.companyName || "",
          companyStatus: "pending",
          companyRequest: req.id,
        });

      } catch (error) {
        this.showMessage(getUserErrorMessage(error, "workers.restoreError", this.$i18n), "error");
      }
    },

    showMessage(text, type) {
      this.message = text;
      this.messageType = type;
    }
  }
};
</script>

<style scoped>
.worker-search-wrap {
  padding: 16px 16px 4px;
}

.worker-search {
  max-width: 520px;
}

.worker-card {
  background-color: #f8f7fd;
  transition: 0.3s;
}
.worker-card:hover {
  background-color: #ece7ff;
}
.worker-accepted {
  background-color: #f1fff4;
}
.worker-rejected {
  background-color: #fff2f2;
}

.worker-list-icon {
  margin-right: 16px;
}

@media (max-width: 600px) {
  .v-container {
    padding-left: 4px !important;
    padding-right: 4px !important;
  }

  .page-header .v-card__title {
    font-size: 1.2rem !important;
    line-height: 1.3;
    padding: 0;
  }

  .workers-panel {
    border-radius: 12px !important;
    overflow: hidden;
  }

  .worker-search-wrap {
    padding: 12px 10px 4px;
  }

  .worker-search {
    max-width: none;
  }

  .workers-tabs {
    overflow-x: auto;
  }

  ::v-deep .workers-tabs .v-slide-group__prev,
  ::v-deep .workers-tabs .v-slide-group__next {
    display: none !important;
  }

  ::v-deep .v-tab {
    flex: 1 0 33.333%;
    font-size: 0.74rem;
    letter-spacing: 0;
    min-width: 0;
    padding: 0 6px;
    white-space: normal;
  }

  ::v-deep .v-tabs-items {
    background: #fff;
  }

  .workers-grid {
    margin: 0 !important;
    padding: 10px;
  }

  .workers-grid .col {
    padding: 6px !important;
  }

  .worker-card {
    border: 1px solid rgba(74, 58, 255, 0.08);
    box-shadow: none !important;
    padding: 12px !important;
  }

  .worker-card-title {
    align-items: flex-start;
    flex-wrap: nowrap;
    overflow-wrap: anywhere;
    padding: 0 !important;
  }

  .worker-card-title .v-avatar {
    flex: 0 0 auto;
    margin-right: 10px !important;
  }

  .worker-identity {
    min-width: 0;
  }

  .worker-name,
  .worker-email {
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .worker-card-actions {
    border-top: 1px solid rgba(63, 61, 86, 0.08);
    gap: 8px;
    justify-content: stretch !important;
    margin-top: 12px;
    padding: 10px 0 0 !important;
  }

  .worker-action-btn {
    background: #fff;
    border: 1px solid rgba(63, 61, 86, 0.12);
    border-radius: 10px;
    flex: 1 1 0;
    height: 38px !important;
    width: auto !important;
  }

  ::v-deep .v-list {
    padding: 10px !important;
  }

  .worker-accepted,
  .worker-rejected {
    align-items: flex-start;
    border: 1px solid rgba(63, 61, 86, 0.08);
    margin-bottom: 10px !important;
    padding: 12px 8px;
  }

  .worker-accepted ::v-deep .v-list-item__content,
  .worker-rejected ::v-deep .v-list-item__content {
    min-width: 0;
  }

  .worker-accepted ::v-deep .v-list-item__title,
  .worker-rejected ::v-deep .v-list-item__title {
    font-size: 0.94rem;
    font-weight: 700;
    line-height: 1.25;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .worker-accepted ::v-deep .v-list-item__subtitle,
  .worker-rejected ::v-deep .v-list-item__subtitle {
    font-size: 0.78rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
    white-space: normal;
  }

  .worker-list-icon {
    flex: 0 0 auto;
    margin-right: 10px;
    margin-top: 2px;
  }

  .worker-list-action {
    flex: 0 0 auto;
    margin-left: 8px;
  }
}

</style>
